import { isValidPhoneNumber } from 'libphonenumber-js';
import { Translate } from 'next-translate';
import { FieldError } from 'react-hook-form';
import { PUBLIC_EMAIL_DOMAINS } from '@utils/consts/form';
import { LengthValidator, Validators, Validator } from '@utils/types/form';

type ErrorMessageProps = Validators & { error: FieldError };

const getErrorMessageData = ({
    error,
    max,
    maxLength,
    min,
    minLength,
}: ErrorMessageProps): [string, null | { [key: string]: string | number }] => {
    const { type } = error;
    const message = error.message || `form.error.${type}`;

    switch (type) {
        case Validator.MAX:
            return [
                message,
                { max: 'number' === typeof max ? max : ((max as LengthValidator).value as number) },
            ];
        case Validator.MAX_LENGTH:
            return [
                message,
                {
                    maxLength:
                        'number' === typeof maxLength
                            ? maxLength
                            : ((maxLength as LengthValidator).value as number),
                },
            ];
        case Validator.MIN:
            return [
                message,
                { min: 'number' === typeof min ? min : ((min as LengthValidator).value as number) },
            ];
        case Validator.MIN_LENGTH:
            return [
                message,
                {
                    minLength:
                        'number' === typeof minLength
                            ? minLength
                            : ((minLength as LengthValidator).value as number),
                },
            ];
        case Validator.PATTERN:
        case Validator.REQUIRED:
        case Validator.VALIDATE:
        default:
            return [message, null];
    }
};

// eslint-disable-next-line import/prefer-default-export
export const getErrorMessage = ({
    error,
    t,
    ...props
}: ErrorMessageProps & { t: Translate }): string => {
    if (!error) {
        return '';
    }

    const [message, data] = getErrorMessageData({ error, ...props });

    return t(message, data);
};

export const validateConfirmPassword = (passwordValue: string, confirmPasswordValue: string) =>
    confirmPasswordValue === passwordValue ? undefined : 'common:form.error.confirmPassword';

type ValidationResult = true | string;

export const validateProfessionalEmail = (value: string): ValidationResult => {
    const domain = value.split('@')[1]?.trim().toLowerCase();

    if (!domain || !PUBLIC_EMAIL_DOMAINS.includes(domain)) {
        return true;
    }

    return 'signup:personalInformation.error.professionalEmail';
};

/**
 * Validate a phone number.
 * @param value The phone number to validate.
 * @returns A validation result - true indicates if the phone number is valid,
 * or a string indicating the error message.
 */
export const validatePhoneNumber = (value: string): ValidationResult => {
    if (!value.startsWith('+')) {
        return 'signup:personalInformation.error.phoneFormat';
    }
    try {
        if (!isValidPhoneNumber(value)) {
            return 'signup:personalInformation.error.phoneInvalid';
        }
    } catch {
        return 'signup:personalInformation.error.phoneFormatValidation';
    }

    return true; // <- must be true, not '' to match react-hook-form validation format
};

/**
 * Convert an ISO 3166-1 alpha-2 country code to a flag emoji.
 * @param iso The ISO 3166-1 alpha-2 country code.
 * @returns The corresponding flag emoji or a default flag if the code is invalid.
 */
export const isoToFlag = (iso: string | undefined): string => {
    if (!iso || !/^[A-Z]{2}$/.test(iso)) return '🏳️';

    return iso
        .toUpperCase()
        .replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));
};
